import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Reference to slides container element
   * @type {HTMLElement|null}
   */
  #slidesContainer = null;

  /**
   * Drag interaction state
   */
  #isDragging = false;
  #startX = 0;
  #startY = 0;
  #scrollLeft = 0;
  #hasMoved = false;
  #isHorizontalDrag = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Add drag/swipe functionality
    this.#slidesContainer = this.querySelector('.announcement-bar__slides');
    if (this.#slidesContainer) {
      this.#slidesContainer.addEventListener('mousedown', this.#handleDragStart);
      this.#slidesContainer.addEventListener('mousemove', this.#handleDragMove);
      this.#slidesContainer.addEventListener('mouseup', this.#handleDragEnd);
      this.#slidesContainer.addEventListener('mouseleave', this.#handleDragEnd);

      // Touch events for mobile
      this.#slidesContainer.addEventListener('touchstart', this.#handleDragStart, { passive: true });
      this.#slidesContainer.addEventListener('touchmove', this.#handleDragMove, { passive: false });
      this.#slidesContainer.addEventListener('touchend', this.#handleDragEnd);
    }

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);

    // Clean up drag/swipe event listeners
    if (this.#slidesContainer) {
      this.#slidesContainer.removeEventListener('mousedown', this.#handleDragStart);
      this.#slidesContainer.removeEventListener('mousemove', this.#handleDragMove);
      this.#slidesContainer.removeEventListener('mouseup', this.#handleDragEnd);
      this.#slidesContainer.removeEventListener('mouseleave', this.#handleDragEnd);
      this.#slidesContainer.removeEventListener('touchstart', this.#handleDragStart);
      this.#slidesContainer.removeEventListener('touchmove', this.#handleDragMove);
      this.#slidesContainer.removeEventListener('touchend', this.#handleDragEnd);
    }

    // Clear interval
    this.suspend();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  /**
   * Handle drag/swipe start
   */
  #handleDragStart = (e) => {
    const slidesContainer = this.querySelector('.announcement-bar__slides');
    if (!slidesContainer) return;

    // Don't interfere with links or buttons
    if (e.target.closest('a, button')) return;

    this.#isDragging = true;
    this.#hasMoved = false;
    this.#isHorizontalDrag = null;
    this.#startX = e.type === 'touchstart' ? e.touches[0].pageX : e.pageX;
    this.#startY = e.type === 'touchstart' ? e.touches[0].pageY : e.pageY;
    this.#scrollLeft = slidesContainer.scrollLeft;

    // Pause autoplay while dragging
    this.suspend();
  };

  /**
   * Handle drag/swipe move
   */
  #handleDragMove = (e) => {
    if (!this.#isDragging) return;

    const slidesContainer = this.querySelector('.announcement-bar__slides');
    if (!slidesContainer) return;

    const x = e.type === 'touchmove' ? e.touches[0].pageX : e.pageX;
    const y = e.type === 'touchmove' ? e.touches[0].pageY : e.pageY;
    const deltaX = x - this.#startX;
    const deltaY = y - this.#startY;

    // Determine drag direction on first significant movement
    if (this.#isHorizontalDrag === null && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
      this.#isHorizontalDrag = Math.abs(deltaX) > Math.abs(deltaY);
    }

    // Only handle horizontal drags, allow vertical scrolling
    if (this.#isHorizontalDrag === false) return;

    const walk = deltaX * 2; // Multiply for faster scroll

    // Only prevent default and scroll if horizontal drag
    if (this.#isHorizontalDrag && Math.abs(walk) > 5) {
      e.preventDefault();
      this.#hasMoved = true;
      slidesContainer.scrollLeft = this.#scrollLeft - walk;
    }
  };

  /**
   * Handle drag/swipe end
   */
  #handleDragEnd = () => {
    if (!this.#isDragging) return;

    this.#isDragging = false;

    const slidesContainer = this.querySelector('.announcement-bar__slides');
    if (!slidesContainer) return;

    // Only snap to slide if user actually dragged (not just clicked)
    if (this.#hasMoved) {
      const slideWidth = slidesContainer.offsetWidth;
      const scrollPosition = slidesContainer.scrollLeft;
      const newIndex = Math.round(scrollPosition / slideWidth);

      this.current = newIndex;
    }

    this.#hasMoved = false;

    // Resume autoplay after drag ends
    this.resume();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
